var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React from "react";
import { Button, Col, ErrorModal, Loading, MessageBanner, Row, TextField, } from "@folio/stripes/components";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormattedMessage, useIntl } from "react-intl";
import { createKult1501ReportSchema } from "../../../utils/validationUtils";
import { generateKult1501Report, generateKult1501Xlsx, } from "../../../api/reportsService";
import { useInlineReportMutation } from "../../../hooks/useInlineReportMutation";
import { useReportMutation } from "../../../hooks/useReportMutation";
import { Kult1501ReportResults } from "./Kult1501ReportResults";
import { ReportSubmitSection } from "./ReportSubmitSection";
import { ReportJobDownload } from "./ReportJobDownload";
import css from "./reports.module.css";
var transformFormToApiParams = function (data) { return ({
    year: Number(data.year),
}); };
var getDefaultValues = function () { return ({
    year: String(new Date().getFullYear()),
}); };
export var Kult1501ReportForm = function () {
    var formatMessage = useIntl().formatMessage;
    var _a = useForm({
        resolver: zodResolver(createKult1501ReportSchema(formatMessage)),
        defaultValues: getDefaultValues(),
    }), control = _a.control, handleSubmit = _a.handleSubmit, getValues = _a.getValues;
    var preview = useInlineReportMutation({
        generateReport: function (ky, data) {
            return generateKult1501Report(ky, transformFormToApiParams(data));
        },
        successMessageId: "ui-br-agency.outputs.kult-15-01.success.preview",
        errorMessageId: "ui-br-agency.outputs.kult-15-01.error.generate",
    });
    var xlsx = useReportMutation({
        reportType: "kult-15-01-export",
        generateReport: function (ky, data) {
            return generateKult1501Xlsx(ky, transformFormToApiParams(data));
        },
        successMessageId: "ui-br-agency.outputs.kult-15-01.success.xlsx",
        errorMessageId: "ui-br-agency.outputs.kult-15-01.error.generate",
    });
    var onDownloadXlsx = function () {
        handleSubmit(function () { return xlsx.mutate(getValues()); })();
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("form", { onSubmit: handleSubmit(function (data) { return preview.mutate(data); }) },
            React.createElement("h2", null,
                React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.report.kult-15-01" })),
            React.createElement("p", null,
                React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.kult-15-01.description" })),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12, md: 4 },
                    React.createElement(Controller, { control: control, name: "year", render: function (_a) {
                            var _b;
                            var field = _a.field, fieldState = _a.fieldState;
                            return (React.createElement(TextField, __assign({}, field, { label: formatMessage({
                                    id: "ui-br-agency.outputs.kult-15-01.field.year",
                                }), required: true, error: (_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message })));
                        } }))),
            xlsx.successMessage && (React.createElement(MessageBanner, { type: "success", className: css.successMessage, dismissible: true, onExited: xlsx.clearSuccessMessage }, xlsx.successMessage)),
            React.createElement(Row, null,
                React.createElement(Col, { xs: 12 },
                    React.createElement("div", { className: css.submitRow },
                        React.createElement(Button, { type: "button", buttonStyle: "primary", onClick: onDownloadXlsx, disabled: preview.isLoading || xlsx.isLoading, className: css.downloadButton, marginBottom0: true }, xlsx.reportJob.isGenerating ? (React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.report-job.generating" })) : (React.createElement(FormattedMessage, { id: "ui-br-agency.outputs.kult-15-01.button.downloadXlsx" }))),
                        xlsx.isLoading && React.createElement(Loading, null),
                        React.createElement(ReportJobDownload, { reportJob: xlsx.reportJob })))),
            React.createElement(ErrorModal, { open: xlsx.errorModalOpen, label: formatMessage({ id: "ui-br-agency.modal.label.error" }), content: xlsx.errorMessage, onClose: xlsx.closeErrorModal }),
            React.createElement(ReportSubmitSection, { isLoading: preview.isLoading, generateButtonId: "ui-br-agency.outputs.kult-15-01.button.preview", successMessage: preview.successMessage, onSuccessDismiss: preview.clearSuccessMessage, errorModalOpen: preview.errorModalOpen, errorMessage: preview.errorMessage, onErrorClose: preview.closeErrorModal, disabled: xlsx.isLoading })),
        preview.data && React.createElement(Kult1501ReportResults, { data: preview.data })));
};
